import { useEffect, useMemo } from 'react'
import { toast } from 'sonner'
import { useConnection } from 'wagmi'

import { checksumAddressForCopy } from '#/lib/address.js'
import { useTestnet } from './testnet.js'
import * as Theme from './theme.js'

type Hotkey = {
  /** Key to match (e.g. `'c'`, `'n'`). Matched via `KeyboardEvent.code`. */
  key: string
  /** Requires ⌘ (Mac) or Ctrl (Windows/Linux). */
  mod?: boolean
  /** Requires Option (Mac) or Alt (Windows/Linux). */
  alt?: boolean
  /** Requires Shift. */
  shift?: boolean
  /** Handler to run when the hotkey fires. */
  handler: () => void
}

/** Registers global keyboard shortcuts. Cleans up on unmount. */
export function useHotkeys(hotkeys: readonly Hotkey[]) {
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (isEditing(e)) return

      for (const hotkey of hotkeys) {
        if (e.code !== `Key${hotkey.key.toUpperCase()}`) continue
        if (hotkey.mod && !e.metaKey && !e.ctrlKey) continue
        if (hotkey.alt && !e.altKey) continue
        if (hotkey.shift && !e.shiftKey) continue
        e.preventDefault()
        hotkey.handler()
        return
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [hotkeys])
}

/** Global keyboard shortcuts. Render once near the app root. */
export function Hotkeys() {
  const { address } = useConnection()
  const { toggle } = useTestnet()

  useHotkeys(
    useMemo(
      () => [
        {
          key: 'a',
          alt: true,
          handler: () => {
            if (!address) return
            navigator.clipboard.writeText(checksumAddressForCopy(address))
            toast.success('Address copied')
          },
        },
        {
          key: 't',
          alt: true,
          handler: toggle,
        },
        {
          key: 'm',
          alt: true,
          handler: () => Theme.set(Theme.resolved() === 'dark' ? 'light' : 'dark'),
        },
      ],
      [address, toggle],
    ),
  )

  return null
}

/** Returns true when focus is inside an editable element. */
function isEditing(e: KeyboardEvent) {
  const target = e.target as HTMLElement | null
  if (!target) return false
  const tag = target.tagName
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true
  return target.isContentEditable
}
